import { useEffect, useRef, useState, useCallback } from 'react';

export default function VizioPairingModal({ ipAddress, deviceName, onDismiss, onPaired }) {
  const [state, setState] = useState('initiating');
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [errorMsg, setErrorMsg] = useState('');
  const [pressed, setPressed] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const cancelledRef = useRef(false);

  const simplifyError = (raw) => {
    if (!raw) return "We couldn't pair with your TV.";
    const lower = raw.toLowerCase();
    if (lower.includes('unreachable') || lower.includes('not respond') || lower.includes('connect')) {
      return "We couldn't reach your TV. Make sure it's on and on the same WiFi.";
    }
    if (lower.includes('pin') || lower.includes('wrong') || lower.includes('timed out')) {
      return "That PIN didn't work. Check your TV and try again.";
    }
    if (lower.includes('pending')) {
      return "Pairing expired. Let's start over.";
    }
    return "We couldn't pair with your TV.";
  };

  const startPairing = useCallback(() => {
    if (!window.QudeAndroid?.startVizioPairing) {
      setState('error');
      setErrorMsg("Couldn't connect — please reopen Qude.");
      return;
    }
    setState('initiating');
    setPinDigits(['', '', '', '']);
    setErrorMsg('');

    setTimeout(() => {
      if (cancelledRef.current) return;
      try {
        const responseStr = window.QudeAndroid.startVizioPairing(ipAddress);
        const response = JSON.parse(responseStr || '{}');
        if (cancelledRef.current) return;
        if (response.success) {
          setState('pin');
          setTimeout(() => inputRefs[0].current?.focus(), 150);
        } else {
          setState('error');
          setErrorMsg(simplifyError(response.error));
        }
      } catch (e) {
        if (!cancelledRef.current) {
          setState('error');
          setErrorMsg("Something went wrong. Try again.");
        }
      }
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipAddress]);

  useEffect(() => {
    cancelledRef.current = false;
    startPairing();
    return () => { cancelledRef.current = true; };
  }, [startPairing]);

  const submitPin = async (pin) => {
    setState('submitting');
    if (!window.QudeAndroid?.completeVizioPairing) {
      setState('error');
      setErrorMsg("Couldn't connect — please reopen Qude.");
      return;
    }
    try {
      const responseStr = window.QudeAndroid.completeVizioPairing(ipAddress, pin);
      const response = JSON.parse(responseStr || '{}');
      if (response.success) {
        setState('success');
        onPaired?.(ipAddress);
        setTimeout(() => onDismiss?.(), 1500);
      } else {
        setState('error');
        setErrorMsg(simplifyError(response.error));
      }
    } catch (e) {
      setState('error');
      setErrorMsg("Something went wrong. Try again.");
    }
  };

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...pinDigits];
    newDigits[index] = digit;
    setPinDigits(newDigits);
    if (digit && index < 3) inputRefs[index + 1].current?.focus();
    if (digit && index === 3) {
      const fullPin = newDigits.join('');
      if (fullPin.length === 4) submitPin(fullPin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setPinDigits(pasted.split(''));
      submitPin(pasted);
    }
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    if (window.QudeAndroid?.cancelVizioPairing) {
      try { window.QudeAndroid.cancelVizioPairing(ipAddress); } catch (e) {}
    }
    onDismiss?.();
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(135deg, #050508 0%, #0f0a1e 50%, #1a0f2e 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, overflowY: 'auto',
  };
  const card = {
    maxWidth: 420, width: '100%',
    background: 'rgba(255,255,255,0.04)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 24, padding: '32px 24px',
    textAlign: 'center', color: '#fff',
  };
  const iconCircle = (variant) => ({
    width: 72, height: 72, margin: '0 auto 24px', borderRadius: '50%',
    background: variant === 'success'
      ? 'linear-gradient(135deg, #4A9EFF, #7c3aed)'
      : variant === 'error'
        ? 'linear-gradient(135deg, #7c3aed, #ec4899)'
        : 'linear-gradient(135deg, #3b82f6, #7c3aed)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32,
    boxShadow: variant === 'success'
      ? '0 10px 40px rgba(74,158,255,0.4)'
      : variant === 'error'
        ? '0 10px 40px rgba(236,72,153,0.4)'
        : '0 10px 40px rgba(124,58,237,0.4)',
  });
  const title = {
    fontSize: 24, fontWeight: 700, margin: '0 0 12px',
    background: 'linear-gradient(135deg, #fff, #a78bfa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  };
  const subtitle = {
    fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.75)', margin: '0 0 24px',
  };
  const primaryBtn = (disabled, isPressed) => ({
    width: '100%', padding: 16, fontSize: 16, fontWeight: 600, color: '#fff',
    background: disabled ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #3b82f6, #7c3aed)',
    border: 'none', borderRadius: 14,
    cursor: disabled ? 'wait' : 'pointer',
    boxShadow: '0 8px 24px rgba(124,58,237,0.35)',
    marginBottom: 10,
    transform: isPressed ? 'scale(0.97)' : 'scale(1)',
    opacity: isPressed ? 0.85 : 1,
    transition: 'transform 0.1s, opacity 0.1s',
  });
  const ghostBtn = {
    width: '100%', padding: 14, fontSize: 14, fontWeight: 500,
    color: 'rgba(255,255,255,0.5)', background: 'transparent', border: 'none', cursor: 'pointer',
  };

  const tvIcon = (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#fff' }}>
      <rect x="2" y="4" width="20" height="14" rx="2"/>
      <path d="M8 22h8"/><path d="M12 18v4"/>
    </svg>
  );
  const checkIcon = (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color: '#fff' }}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  const xIcon = (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: '#fff' }}>
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );

  if (state === 'initiating') {
    return (
      <div style={overlay}>
        <div style={card}>
          <div style={iconCircle('default')}>{tvIcon}</div>
          <h1 style={title}>Pair your Vizio</h1>
          <p style={subtitle}>Look at your TV — a 4-digit code will appear on screen.</p>
          <div style={{
            background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
            borderRadius: 16, padding: 14, marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#a78bfa', animation: 'qudePulse 1.5s ease-in-out infinite' }} />
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
              Connecting to {deviceName || 'your TV'}...
            </span>
          </div>
          <button onClick={handleCancel} style={ghostBtn}>Cancel</button>
        </div>
        <style>{`@keyframes qudePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.8)} }`}</style>
      </div>
    );
  }

  if (state === 'pin' || state === 'submitting') {
    const isSubmitting = state === 'submitting';
    return (
      <div style={overlay}>
        <div style={card}>
          <div style={iconCircle('default')}>{tvIcon}</div>
          <h1 style={title}>Enter the code</h1>
          <p style={subtitle}>Type the 4-digit code shown on {deviceName || 'your Vizio'}.</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
            {[0,1,2,3].map((i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={pinDigits[i]}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                disabled={isSubmitting}
                style={{
                  width: 52, height: 64, fontSize: 28, fontWeight: 600,
                  textAlign: 'center', color: '#fff',
                  background: 'rgba(255,255,255,0.04)',
                  border: pinDigits[i] ? '2px solid rgba(167,139,250,0.7)' : '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 14, outline: 'none', caretColor: '#a78bfa',
                  transition: 'border-color 0.15s',
                }}
              />
            ))}
          </div>
          {isSubmitting && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 16 }}>Pairing...</div>
          )}
          <button onClick={handleCancel} style={ghostBtn} disabled={isSubmitting}>Cancel</button>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div style={overlay}>
        <div style={card}>
          <div style={iconCircle('success')}>{checkIcon}</div>
          <h1 style={title}>You're paired!</h1>
          <p style={subtitle}>Qude can now see what's playing on {deviceName || 'this TV'}.</p>
          <div style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 16, padding: 14 }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>All set — closing in a moment.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={iconCircle('error')}>{xIcon}</div>
        <h1 style={title}>Something's not right</h1>
        <p style={subtitle}>{errorMsg}</p>
        <button
          onPointerDown={() => setPressed(true)}
          onPointerUp={() => { setPressed(false); startPairing(); }}
          onPointerLeave={() => setPressed(false)}
          style={primaryBtn(false, pressed)}
        >
          Try again
        </button>
        <button onClick={handleCancel} style={ghostBtn}>Cancel</button>
      </div>
    </div>
  );
}